/**
 * The Z5/Z6 host (docs/audits/PILOT-QUOTES-SHELL.md §2.1-§2.2, S5 row: "MainLayout.jsx (sibling
 * host in the flex row)"). MainLayout wraps <main> in <ToolHost>: the content area holds <main>
 * and, when a tool is open as a column, the panel beside it; the rail sits to the right of the
 * area. Both are outside <main>, so they follow the app theme.
 *
 * A page offers tools by registering them (useToolHost) with its context — { quote, user,
 * layout, … } and its module id — while it is mounted. Nothing registered: no rail. The tools
 * shown, and their order, are the user's toolRail in that module's document; the panel's column
 * width is its panes.toolPanelWidth. Which tool is open is tab state only.
 *
 * Desktop (viewport ≥ RAIL_DEFAULT_BELOW): the 44 px rail, and the panel as a column when it
 * fits (toolGeometry.toolPanelLayout), else the sheet. The tablet: no rail; the page's "Tools"
 * button (ToolsMenuButton) opens a menu of the same tools, and the panel is always the sheet.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Wrench } from 'lucide-react';
import Flyout from '../Flyout';
import ToolRail from './ToolRail';
import ToolPanel from './ToolPanel';
import useViewportWidth from './useViewportWidth';
import { useToolHostState, useToolHostStore } from './toolHostStore';
import { orderedTools, toolColumnRoom, toolPanelLayout, TOOL_COLUMN_MAX, TOOL_RAIL_WIDTH } from './toolGeometry';
import { usePreferences } from '../../../contexts/PreferencesContext';
import { RAIL_DEFAULT_BELOW } from '../../../utils/sidebarLayout';
import './tools.css';

export { ToolHostProvider } from './toolHostStore';

/**
 * Register a page's tools for as long as it is mounted.
 * @param {{ tools: Array, ctx: object, module: string, title?: string }} registration — module is
 *   the registry id whose document holds toolRail and the panel width (G0: required; there is no
 *   default, so a page can never read another module's tools). ctx should be memoised; title
 *   names the tablet's Tools control for assistive tech ("Quote tools": the module rail already
 *   has a section called Tools)
 */
export function useToolHost({ tools, ctx, module, title = 'Tools' }) {
  if (!module) {
    const message = 'useToolHost: `module` is required (the registry id whose document holds toolRail)';
    // A programming error: loud in development and tests, and in production no rail at all
    // rather than another module's tools and widths.
    if (process.env.NODE_ENV !== 'production') throw new Error(message);
    // eslint-disable-next-line no-console
    console.error(message);
  }
  const store = useToolHostStore();
  useLayoutEffect(() => {
    if (store && module) store.setRegistration({ tools, ctx, module, title });
  }, [store, tools, ctx, module, title]);
  useLayoutEffect(() => () => { if (store) store.setRegistration(null); }, [store]);
}

/**
 * The preferences of the registered page's module. With nothing registered there are no tools,
 * so the shell document stands in: it is always loaded, and nothing here reads tools from it.
 */
function useRegisteredModulePrefs(registration) {
  return usePreferences((registration && registration.module) || 'shell');
}

/** The tools this user shows for the registered page, in their order. */
function useShownTools() {
  const { registration } = useToolHostState();
  const { doc } = useRegisteredModulePrefs(registration);
  return useMemo(
    () => (registration ? orderedTools(registration.tools, doc.toolRail) : []),
    [registration, doc.toolRail]
  );
}

export function ToolHost({ children }) {
  const store = useToolHostStore();
  const { registration, openId } = useToolHostState();
  const { doc, update } = useRegisteredModulePrefs(registration);
  const shown = useShownTools();
  const viewport = useViewportWidth();
  const desktop = viewport >= RAIL_DEFAULT_BELOW;
  const areaRef = useRef(null);
  const [areaWidth, setAreaWidth] = useState(0);
  const [dragWidth, setDragWidth] = useState(null);
  const lastKept = useRef(null);

  // W: the content area beside the rail (main plus the column), however the panel is shown.
  useLayoutEffect(() => {
    const el = areaRef.current;
    if (!el) return undefined;
    const measure = () => setAreaWidth(Math.round(el.getBoundingClientRect().width));
    measure();
    window.addEventListener('resize', measure);
    let ro = null;
    if (typeof ResizeObserver === 'function') {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    return () => {
      window.removeEventListener('resize', measure);
      if (ro) ro.disconnect();
    };
  }, []);

  const ctx = registration ? registration.ctx : null;
  const layout = (ctx && ctx.layout) || 'single';
  const openTool = shown.find((t) => t.id === openId) || null;
  if (openTool && openTool.keepMounted) lastKept.current = openTool;
  const stored = doc.panes && Number.isFinite(doc.panes.toolPanelWidth) ? doc.panes.toolPanelWidth : null;
  const geo = toolPanelLayout({ areaWidth, layout, viewportWidth: viewport, storedWidth: stored });
  const maxWidth = Math.min(TOOL_COLUMN_MAX, toolColumnRoom(areaWidth, layout));
  const width = geo.mode === 'column' && dragWidth !== null ? dragWidth : geo.width;
  const columnWidth = openTool && geo.mode === 'column' ? width : 0;

  useLayoutEffect(() => {
    if (store) store.setColumnWidth(columnWidth);
  }, [store, columnWidth]);

  // A tool that is no longer shown (hidden in the popover, the page gone) closes.
  useEffect(() => {
    if (store && openId && !openTool) store.close({ returnFocus: false });
  }, [store, openId, openTool]);

  const close = useCallback(() => store && store.close(), [store]);
  const closeOutside = useCallback(() => store && store.close({ returnFocus: false }), [store]);
  const commitWidth = useCallback((w) => {
    setDragWidth(null);
    update((d) => ({ ...d, panes: { ...(d.panes || {}), toolPanelWidth: w } }));
  }, [update]);

  const panelTool = openTool || (lastKept.current && shown.includes(lastKept.current) ? lastKept.current : null);
  const panel = panelTool && ctx ? (
    <div className={openTool ? 'tool-panel-slot' : 'tool-panel-slot tool-panel-kept'} hidden={!openTool}>
      <ToolPanel
        tool={panelTool}
        ctx={ctx}
        mode={geo.mode}
        width={width}
        maxWidth={maxWidth}
        sheetRight={desktop && shown.length > 0 ? TOOL_RAIL_WIDTH : 0}
        onClose={close}
        onOutside={closeOutside}
        onResize={setDragWidth}
        onResizeEnd={commitWidth}
      />
    </div>
  ) : null;

  return (
    <>
      <div ref={areaRef} data-tool-area="" className="tool-area">
        {children}
        {panel}
      </div>
      {desktop && shown.length > 0 && ctx && (
        <ToolRail
          tools={shown}
          ctx={ctx}
          openId={openId}
          onToggle={(id, el) => store.toggle(id, el)}
        />
      )}
    </>
  );
}

/**
 * The tablet's "Tools" control, for a page header: below RAIL_DEFAULT_BELOW, where there is no
 * rail, a button opening a menu of the same tools; a choice opens the panel (the sheet).
 * Renders nothing on the desktop, with no tools shown, or outside a host. The menu is kept 8 px
 * inside the viewport (tablet pass 2026-10-20, item 2): the button sits near the right edge.
 */
export function ToolsMenuButton() {
  const store = useToolHostStore();
  const { registration } = useToolHostState();
  const shown = useShownTools();
  const viewport = useViewportWidth();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);

  if (!store || !registration || viewport >= RAIL_DEFAULT_BELOW || shown.length === 0) return null;
  const { ctx } = registration;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="tools-menu-button"
        aria-label={registration.title || 'Tools'}
        aria-haspopup="menu"
        aria-expanded={open}
        data-tool-trigger=""
        onClick={() => setOpen((o) => !o)}
      >
        <Wrench aria-hidden="true" />
        Tools
      </button>
      <Flyout
        open={open}
        label={registration.title || 'Tools'}
        placement="below"
        triggerRef={buttonRef}
        viewportMargin={8}
        onClose={() => setOpen(false)}
      >
        <ul className="shell-flyout-list" role="none">
          {shown.map((tool) => {
            const available = tool.availableFor(ctx);
            const ok = available === true;
            return (
              <li key={tool.id} role="none">
                <button
                  type="button"
                  role="menuitem"
                  className="shell-flyout-item tools-menu-item"
                  aria-disabled={!ok}
                  title={ok ? tool.label : `${tool.label}: ${available}`}
                  data-tool-trigger=""
                  onClick={(e) => {
                    if (!ok) {
                      e.preventDefault();
                      e.stopPropagation(); // not a selection: the menu stays open
                      return;
                    }
                    store.open(tool.id, buttonRef.current);
                  }}
                >
                  {tool.label}
                </button>
              </li>
            );
          })}
        </ul>
      </Flyout>
    </>
  );
}
